/**
 * Audiobook Player — in-place body-word read-along
 *
 * Initializes for each [data-audiobook] section:
 *   - Custom audio controls (play/pause, ±15s skip, progress bar, speed)
 *   - When data-readalong JSON is present:
 *       • Wraps words in [data-article-body] <p> elements into <span class="ra-w">
 *       • Aligns narration tokens → body spans using difflib-style longest-matching-block
 *       • Highlights the active word in-place as audio plays (Apple Books style)
 *       • Auto-scrolls active word into view (4s manual-scroll suspension)
 *       • Click a timed word → seek + play
 *   - Sticky mini-bar (play/pause + thin progress) when player scrolled out of view while playing
 */

const RATES = [1, 1.25, 1.5, 2];

function formatTime(seconds) {
	const s = Math.floor(seconds);
	const m = Math.floor(s / 60);
	const rem = s % 60;
	return m + ':' + String(rem).padStart(2, '0');
}

function clamp(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Binary search: find index of the word whose [s, e) bracket contains time.
// Returns -1 if no word is active.
function bsearchActiveWord(words, time) {
	let lo = 0;
	let hi = words.length - 1;
	while (lo <= hi) {
		const mid = (lo + hi) >>> 1;
		const w = words[mid];
		if (time < w.s) {
			hi = mid - 1;
		} else if (time >= w.e) {
			lo = mid + 1;
		} else {
			return mid;
		}
	}
	return -1;
}

// Find index of last word whose .s <= time (for .is-read coloring).
function bsearchLastBefore(words, time) {
	let lo = 0;
	let hi = words.length - 1;
	let result = -1;
	while (lo <= hi) {
		const mid = (lo + hi) >>> 1;
		if (words[mid].s <= time) {
			result = mid;
			lo = mid + 1;
		} else {
			hi = mid - 1;
		}
	}
	return result;
}

// Normalize a token string for matching: lowercase, strip leading/trailing punctuation.
function normToken(str) {
	return str
		.toLowerCase()
		.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

/**
 * Wrap words inside each <p> in bodyEl into <span class="ra-w"> elements.
 * Text outside paragraphs (headings, figcaptions, blockquotes, sups) is untouched.
 * Returns body[] = Array<{norm: string, el: HTMLSpanElement}>
 */
function wrapBodyWords(bodyEl) {
	const body = [];
	// Word regex: starts with a letter, may contain letters, digits, apostrophes, hyphens, dots
	const wordRe = /\p{L}[\p{L}\p{N}'’.\-@]*/gu;

	const paragraphs = bodyEl.querySelectorAll('p');
	paragraphs.forEach((para) => {
		// Collect all text nodes (direct and nested, but not in <sup> or <a> footnote refs)
		const walker = document.createTreeWalker(
			para,
			NodeFilter.SHOW_TEXT,
			{
				acceptNode(node) {
					// Skip text inside <sup> (footnote superscripts)
					let p = node.parentElement;
					while (p && p !== para) {
						if (p.tagName === 'SUP') return NodeFilter.FILTER_REJECT;
						p = p.parentElement;
					}
					return NodeFilter.FILTER_ACCEPT;
				},
			}
		);

		const textNodes = [];
		let node;
		while ((node = walker.nextNode())) {
			textNodes.push(node);
		}

		// Process each text node: split into word/non-word fragments
		textNodes.forEach((textNode) => {
			const text = textNode.textContent;
			if (!text.trim()) return;

			wordRe.lastIndex = 0;
			const fragments = [];
			let lastIndex = 0;
			let match;

			while ((match = wordRe.exec(text)) !== null) {
				// text before this word
				if (match.index > lastIndex) {
					fragments.push(document.createTextNode(text.slice(lastIndex, match.index)));
				}
				// the word span
				const span = document.createElement('span');
				span.className = 'ra-w';
				span.textContent = match[0];
				const norm = normToken(match[0]);
				if (norm) {
					body.push({ norm, el: span });
				}
				fragments.push(span);
				lastIndex = match.index + match[0].length;
			}

			// remaining text after last word
			if (lastIndex < text.length) {
				fragments.push(document.createTextNode(text.slice(lastIndex)));
			}

			if (fragments.length > 0) {
				const parent = textNode.parentNode;
				fragments.forEach((f) => parent.insertBefore(f, textNode));
				parent.removeChild(textNode);
			}
		});
	});

	return body;
}

/**
 * Difflib-style longest-matching-block alignment of narration tokens → body tokens.
 * Returns timed[] = Array<{s, e, el}> sorted by s, for binary search during playback.
 *
 * Algorithm: recursive longest-common-subsequence matching block finding.
 * Builds an index of body-norm → positions (b2j), then finds the longest run
 * of consecutive matching tokens, recurses left/right of the match.
 * Autojunk disabled (narration is ~2770 tokens, body ~2914 — no need for junk heuristics).
 */
function alignNarrationToBody(narr, body) {
	// b2j: norm → sorted array of body indices
	const b2j = new Map();
	body.forEach((bw, i) => {
		if (!b2j.has(bw.norm)) b2j.set(bw.norm, []);
		b2j.get(bw.norm).push(i);
	});

	const matches = []; // {ai, bi, len} — matched block positions

	function findLongestMatch(alo, ahi, blo, bhi) {
		let bestSize = 0;
		let bestAi = alo;
		let bestBi = blo;

		// j2len: for each body position j, longest run of matches ending at j.
		// MUST be replaced (not merged) each i — difflib semantics: a run only
		// extends if the match is contiguous in the immediately previous row.
		let j2len = new Map();

		for (let i = alo; i < ahi; i++) {
			const norm = narr[i].norm;
			const positions = b2j.get(norm);
			if (!positions) continue;

			const newj2len = new Map();
			// positions is sorted; only look at those in [blo, bhi)
			// binary search for blo
			let plo = 0;
			let phi = positions.length;
			while (plo < phi) {
				const mid = (plo + phi) >>> 1;
				if (positions[mid] < blo) plo = mid + 1;
				else phi = mid;
			}
			for (let pi = plo; pi < positions.length; pi++) {
				const j = positions[pi];
				if (j >= bhi) break;
				const k = (j2len.get(j - 1) || 0) + 1;
				newj2len.set(j, k);
				if (k > bestSize) {
					bestSize = k;
					bestAi = i - k + 1;
					bestBi = j - k + 1;
				}
			}
			// replace j2len with this row's runs (difflib: j2len = newj2len)
			j2len = newj2len;
		}

		if (bestSize === 0) return;

		// Recurse left of the match
		if (alo < bestAi && blo < bestBi) {
			findLongestMatch(alo, bestAi, blo, bestBi);
		}

		matches.push({ ai: bestAi, bi: bestBi, len: bestSize });

		// Recurse right of the match
		if (bestAi + bestSize < ahi && bestBi + bestSize < bhi) {
			findLongestMatch(bestAi + bestSize, ahi, bestBi + bestSize, bhi);
		}
	}

	findLongestMatch(0, narr.length, 0, body.length);

	// Sort matches by narration position (they come out in recursive order)
	matches.sort((a, b) => a.ai - b.ai);

	// Build timed[] from matched blocks
	const timed = [];
	matches.forEach(({ ai, bi, len }) => {
		for (let k = 0; k < len; k++) {
			const nw = narr[ai + k];
			const bw = body[bi + k];
			bw.el.dataset.seekable = '1';
			bw.el.dataset.s = nw.s;
			timed.push({ s: nw.s, e: nw.e, el: bw.el });
		}
	});

	// timed is already sorted because matches are sorted and runs are consecutive
	return timed;
}

function initPlayer(section) {
	const audioSrc = section.dataset.audio;
	const readalongSrc = section.dataset.readalong || null;

	const audio = new Audio(audioSrc);
	audio.preload = 'metadata';

	// Control elements
	const playBtn = section.querySelector('[data-ab-play]');
	const backBtn = section.querySelector('[data-ab-back]');
	const fwdBtn = section.querySelector('[data-ab-fwd]');
	const progressTrack = section.querySelector('[data-ab-progress]');
	const progressFill = section.querySelector('[data-ab-fill]');
	const curLabel = section.querySelector('[data-ab-cur]');
	const durLabel = section.querySelector('[data-ab-dur]');
	const rateBtn = section.querySelector('[data-ab-rate]');

	let rateIndex = 0;
	let rafId = null;

	// ---------- Sticky mini-bar ----------

	const stickyBar = document.createElement('div');
	stickyBar.className = 'audiobook__sticky';
	stickyBar.hidden = true;
	stickyBar.setAttribute('aria-hidden', 'true');

	const stickyPlay = document.createElement('button');
	stickyPlay.type = 'button';
	stickyPlay.className = 'audiobook__sticky-play';
	stickyPlay.setAttribute('aria-label', playBtn.getAttribute('aria-label'));

	const stickyFill = document.createElement('div');
	stickyFill.className = 'audiobook__sticky-fill';

	const stickyProgress = document.createElement('div');
	stickyProgress.className = 'audiobook__sticky-progress';
	stickyProgress.appendChild(stickyFill);

	const stickyTime = document.createElement('span');
	stickyTime.className = 'audiobook__sticky-time';
	stickyTime.textContent = '0:00';

	stickyBar.appendChild(stickyPlay);
	stickyBar.appendChild(stickyProgress);
	stickyBar.appendChild(stickyTime);
	document.body.appendChild(stickyBar);

	stickyPlay.addEventListener('click', () => {
		if (audio.paused) audio.play();
		else audio.pause();
	});

	function updateStickyVisibility() {
		const rect = section.getBoundingClientRect();
		const sectionVisible = rect.bottom > 0 && rect.top < window.innerHeight;
		if (!audio.paused && !sectionVisible) {
			stickyBar.hidden = false;
			stickyBar.removeAttribute('aria-hidden');
		} else {
			stickyBar.hidden = true;
			stickyBar.setAttribute('aria-hidden', 'true');
		}
	}

	window.addEventListener('scroll', updateStickyVisibility, { passive: true });

	function updateStickyState() {
		if (audio.paused) {
			stickyBar.hidden = true;
			stickyBar.setAttribute('aria-hidden', 'true');
			stickyBar.classList.remove('is-playing');
		} else {
			stickyBar.classList.add('is-playing');
			updateStickyVisibility();
		}
	}

	// ---------- Playback state ----------

	function updatePlayState() {
		if (audio.paused) {
			section.classList.remove('is-playing');
		} else {
			section.classList.add('is-playing');
		}
		updateStickyState();
	}

	function togglePlay() {
		if (audio.paused) {
			audio.play();
		} else {
			audio.pause();
		}
	}

	playBtn.addEventListener('click', togglePlay);
	audio.addEventListener('play', updatePlayState);
	audio.addEventListener('pause', updatePlayState);

	// ---------- Skip ----------

	backBtn.addEventListener('click', () => {
		audio.currentTime = clamp(audio.currentTime - 15, 0, audio.duration || 0);
	});

	fwdBtn.addEventListener('click', () => {
		audio.currentTime = clamp(audio.currentTime + 15, 0, audio.duration || 0);
	});

	// ---------- Progress bar ----------

	audio.addEventListener('loadedmetadata', () => {
		durLabel.textContent = formatTime(audio.duration);
	});

	audio.addEventListener('timeupdate', () => {
		if (!audio.duration) return;
		const pct = (audio.currentTime / audio.duration) * 100;
		progressFill.style.width = pct + '%';
		stickyFill.style.width = pct + '%';
		curLabel.textContent = formatTime(audio.currentTime);
		stickyTime.textContent = formatTime(audio.currentTime);
		progressTrack.setAttribute('aria-valuenow', Math.round(pct));
	});

	function seekFromPointer(e) {
		if (!audio.duration) return;
		const rect = progressTrack.getBoundingClientRect();
		const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
		const pct = clamp(x / rect.width, 0, 1);
		audio.currentTime = pct * audio.duration;
	}

	progressTrack.setAttribute('aria-valuemin', '0');
	progressTrack.setAttribute('aria-valuemax', '100');
	progressTrack.setAttribute('aria-valuenow', '0');

	let isDragging = false;
	progressTrack.addEventListener('mousedown', (e) => {
		isDragging = true;
		seekFromPointer(e);
	});
	progressTrack.addEventListener('touchstart', (e) => {
		isDragging = true;
		seekFromPointer(e);
	}, { passive: true });
	document.addEventListener('mousemove', (e) => { if (isDragging) seekFromPointer(e); });
	document.addEventListener('touchmove', (e) => { if (isDragging) seekFromPointer(e); }, { passive: true });
	document.addEventListener('mouseup', () => { isDragging = false; });
	document.addEventListener('touchend', () => { isDragging = false; });

	progressTrack.addEventListener('keydown', (e) => {
		if (!audio.duration) return;
		const step = audio.duration * 0.01;
		if (e.key === 'ArrowRight') { audio.currentTime = clamp(audio.currentTime + step, 0, audio.duration); e.preventDefault(); }
		else if (e.key === 'ArrowLeft') { audio.currentTime = clamp(audio.currentTime - step, 0, audio.duration); e.preventDefault(); }
		else if (e.key === 'Home') { audio.currentTime = 0; e.preventDefault(); }
		else if (e.key === 'End') { audio.currentTime = audio.duration; e.preventDefault(); }
	});

	// ---------- Playback rate ----------

	rateBtn.addEventListener('click', () => {
		rateIndex = (rateIndex + 1) % RATES.length;
		const rate = RATES[rateIndex];
		audio.playbackRate = rate;
		audio.preservesPitch = true;
		if ('mozPreservesPitch' in audio) audio.mozPreservesPitch = true;
		if ('webkitPreservesPitch' in audio) audio.webkitPreservesPitch = true;
		rateBtn.textContent = rate + '×';
	});

	// ---------- Read-along: in-place body word highlighting ----------

	if (!readalongSrc) return;

	const bodyEl = document.querySelector('[data-article-body]');
	if (!bodyEl) return;

	// Wrap paragraph words into <span class="ra-w"> spans
	const body = wrapBodyWords(bodyEl);
	if (!body.length) return;

	let timed = []; // subset of body spans that carry timing; sorted by s
	let activeEl = null; // the span currently marked .is-active
	let readUpTo = -1; // timed[0..readUpTo] currently carry .is-read
	let scrollSuspendedUntil = 0;

	// Suspend auto-scroll when user scrolls the page manually
	window.addEventListener('scroll', () => {
		if (!audio.paused) {
			scrollSuspendedUntil = Date.now() + 4000;
		}
	}, { passive: true });

	// Fetch JSON, align, build timed[]
	fetch(readalongSrc)
		.then((res) => res.json())
		.then((data) => {
			// Build narr[] flat array of {norm, s, e}
			const narr = [];
			for (const segment of data.segments) {
				for (const word of segment.words) {
					const norm = normToken(word.t);
					if (norm) narr.push({ norm, s: word.s, e: word.e });
				}
			}

			// Run alignment (may take ~10-50ms for 2770×2914 tokens)
			timed = alignNarrationToBody(narr, body);

			// Wire click-to-seek on timed words
			timed.forEach(({ s, el }) => {
				el.addEventListener('click', () => {
					audio.currentTime = s;
					audio.play();
				});
			});
		})
		.catch((err) => {
			console.warn('[audiobook] Failed to load read-along JSON:', err);
		});

	// Bring timed[0..idx] to .is-read and timed[idx+1..] to not-read, adjusting
	// only the delta. Handles both forward progress and backward seeks.
	function setReadBoundary(idx) {
		if (idx > readUpTo) {
			for (let i = readUpTo + 1; i <= idx; i++) timed[i].el.classList.add('is-read');
		} else if (idx < readUpTo) {
			for (let i = idx + 1; i <= readUpTo; i++) timed[i].el.classList.remove('is-read');
		}
		readUpTo = idx;
	}

	// Reconcile DOM highlight state to the current playback time. Idempotent —
	// safe to call from the rAF loop and on single seek events while paused.
	function renderHighlight() {
		if (!timed.length) return;
		const time = audio.currentTime;
		const activeIdx = bsearchActiveWord(timed, time);
		const newActiveEl = activeIdx >= 0 ? timed[activeIdx].el : null;

		if (newActiveEl !== activeEl) {
			if (activeEl) activeEl.classList.remove('is-active');
			if (newActiveEl) {
				newActiveEl.classList.add('is-active');
				newActiveEl.classList.remove('is-read');
				if (Date.now() >= scrollSuspendedUntil) {
					newActiveEl.scrollIntoView({
						behavior: prefersReducedMotion() ? 'auto' : 'smooth',
						block: 'center',
					});
				}
			}
			activeEl = newActiveEl;
		}

		// Words strictly before the playhead are "read"; the active word is not.
		const readIdx = activeIdx >= 0 ? activeIdx - 1 : bsearchLastBefore(timed, time);
		setReadBoundary(readIdx);
		if (activeEl) activeEl.classList.remove('is-read');
	}

	function tick() {
		renderHighlight();
		rafId = requestAnimationFrame(tick);
	}

	audio.addEventListener('play', () => {
		if (!rafId) rafId = requestAnimationFrame(tick);
	});

	audio.addEventListener('pause', () => {
		if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
	});

	audio.addEventListener('ended', () => {
		if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
		section.classList.remove('is-playing');
	});

	// Keep the highlight correct after a seek even while paused (no rAF running).
	audio.addEventListener('seeked', renderHighlight);
}

export function initAudiobooks() {
	document.querySelectorAll('[data-audiobook]').forEach(initPlayer);
}
